import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StarRating from "@/components/StarRating";
import ShareButton from "@/components/ShareButton";
import DealCountdown from "@/components/DealCountdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { MapPin, Phone, Globe, Clock, Heart, Flag, Ticket, QrCode, Loader2, ShoppingBag, MessageSquare, Trash2, Sparkles, Facebook, Instagram } from "lucide-react";
import { API } from "@/lib/api";
import { UpgradeNudge, ContactForm, BizChat, visitorRef } from "@/components/ListingTierParts";

function setMeta(name, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function BusinessProfile() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { user, openAuth } = useAuth();
  const [b, setB] = useState(null);
  const [highlight, setHighlight] = useState(params.get("from") === "assistant");
  const [reviews, setReviews] = useState([]);
  const [comments, setComments] = useState([]);
  const [saved, setSaved] = useState(false);
  const [summary, setSummary] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [commentText, setCommentText] = useState("");
  const [busy, setBusy] = useState(false);

  const topComments = useMemo(() => comments.filter((c) => !c.parent_id), [comments]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/business/${slug}`.replace("/business/", "/businesses/"));
    setB(data);
    const [r, c] = await Promise.all([
      api.get(`/businesses/${data.id}/reviews`),
      api.get(`/businesses/${data.id}/comments`),
    ]);
    setReviews(r.data); setComments(c.data);
    api.post(`/businesses/${data.id}/view`).catch(() => {});
    api.get(`/businesses/${data.id}/review-summary`).then(({ data: s }) => setSummary(s.summary)).catch(() => {});
    if (user) {
      const ids = await api.get("/favorites/ids").then((x) => x.data).catch(() => []);
      setSaved(ids.includes(data.id));
    }
  }, [slug, user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (highlight) { const t = setTimeout(() => setHighlight(false), 5000); return () => clearTimeout(t); }
  }, [highlight]);

  useEffect(() => {
    if (!b) return;
    const loc = b.city ? `${b.city}, ${b.state_abbr}` : b.state_abbr || "";
    const title = `${b.name}${loc ? ` — ${loc}` : ""} | Saving America Money`;
    const desc = b.short_description || `${b.name}${b.category ? ` · ${b.category}` : ""}${loc ? ` in ${loc}` : ""}. Reviews, deals and contact info on Saving America Money.`;
    const enc = encodeURIComponent;
    const rating = b.avg_rating ? Number(b.avg_rating).toFixed(1) : null;
    const feat = rating ? `${rating} rating · ${b.review_count || 0} ${(b.review_count === 1) ? "review" : "reviews"}` : "";
    const subtitle = `${b.category || "Local business"}${loc ? ` · ${loc}` : ""}`;
    const deal = b.postcard_deal_headline ? String(b.postcard_deal_headline) : "";
    const ogImg = `${process.env.REACT_APP_BACKEND_URL}/api/og-image?kind=business&title=${enc(b.name)}&subtitle=${enc(subtitle)}${feat ? `&featured=${enc(feat)}` : ""}${deal ? `&deal=${enc(deal)}` : ""}`;
    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:image", ogImg, "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", ogImg);
    return () => {
      document.title = "Saving America Money";
      ['meta[property="og:title"]', 'meta[property="og:description"]', 'meta[property="og:image"]',
       'meta[property="og:image:width"]', 'meta[property="og:image:height"]',
       'meta[name="twitter:card"]', 'meta[name="twitter:image"]'].forEach((s) => document.head.querySelector(s)?.remove());
    };
  }, [b]);

  useEffect(() => {
    if (!b) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: b.name,
      description: b.short_description || b.full_description || undefined,
      url: `${window.location.origin}/business/${b.slug}`,
      telephone: (b.features?.phone && b.phone) || undefined,
      image: b.photo_urls?.[0] || undefined,
      priceRange: b.price_range || undefined,
      address: {
        "@type": "PostalAddress",
        streetAddress: b.address_line || undefined,
        addressLocality: b.city || undefined,
        addressRegion: b.state_abbr || undefined,
        postalCode: b.zip || undefined,
        addressCountry: "US",
      },
    };
    if (b.latitude && b.longitude) {
      ld.geo = { "@type": "GeoCoordinates", latitude: b.latitude, longitude: b.longitude };
    }
    if (b.avg_rating > 0 && b.review_count > 0) {
      ld.aggregateRating = { "@type": "AggregateRating", ratingValue: Number(b.avg_rating).toFixed(1), reviewCount: b.review_count };
    }
    document.getElementById("sam-ld-business")?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "sam-ld-business";
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { document.getElementById("sam-ld-business")?.remove(); };
  }, [b]);

  if (!b) return <div className="h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;

  const isOwner = user && (user.user_id === b.owner_user_id || user.role === "admin");
  const isPremium = b.listing_plan === "premium_online";
  const refCode = visitorRef(params);
  const trackCall = () => { api.post(`/businesses/${b.id}/track`, { kind: "call_click", ref: refCode }).catch(() => {}); };
  const outUrl = (kind) => `${API}/out/${b.id}/${kind}${refCode ? `?ref=${encodeURIComponent(refCode)}` : ""}`;

  const toggleSave = async () => {
    if (!user) return openAuth();
    const { data } = await api.post(`/favorites/${b.id}`);
    setSaved(data.favorited);
    toast(data.favorited ? "Saved to your list" : "Removed from saved");
  };

  const submitReview = async () => {
    if (!user) return openAuth();
    if (!rating) return toast.error("Pick a star rating");
    setBusy(true);
    try {
      await api.post(`/businesses/${b.id}/reviews`, { rating, text: reviewText });
      setReviewText(""); setRating(0);
      await load();
      toast.success("Review posted");
    } finally { setBusy(false); }
  };

  const submitComment = async () => {
    if (!user) return openAuth();
    if (!commentText.trim()) return;
    await api.post(`/businesses/${b.id}/comments`, { text: commentText });
    setCommentText(""); await load();
  };

  const reply = async (rid, text) => { await api.post(`/reviews/${rid}/reply`, { text }); await load(); toast.success("Reply posted"); };
  const del = async (rid) => { await api.delete(`/reviews/${rid}`); await load(); };
  const flag = async (rid) => { await api.post(`/reviews/${rid}/flag`); toast("Flagged for review"); };

  return (
    <div>
      {/* Hero */}
      <div className="relative h-64 sm:h-80 bg-sam-navy">
        {b.photo_urls?.[0]
          ? <img src={b.photo_urls[0]} alt={b.name} className="h-full w-full object-cover opacity-80" />
          : <div className="h-full w-full bg-gradient-to-br from-sam-navy to-sam-navy2" />}
        <div className="absolute inset-0 bg-gradient-to-t from-sam-navy via-sam-navy/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 pb-6 text-sam-cream">
            <div className="flex items-center gap-2 mb-2">
              {b.listing_plan === "postcard" && <Badge className="bg-sam-crimson">On the Postcard</Badge>}
              {isPremium && <Badge className="bg-sam-amber">Premium</Badge>}
              {b.featured_pick && <Badge className="bg-white text-sam-navy">Featured</Badge>}
            </div>
            <h1 className={`font-display text-3xl sm:text-5xl font-extrabold transition-all ${highlight ? "rounded-lg ring-2 ring-sam-amber ring-offset-2 ring-offset-sam-navy px-1 -mx-1" : ""}`}>{b.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <Link to={`/browse?parent=${b.parent_slug}`} className="font-semibold text-sam-cream/90 hover:text-white">{b.parent_category}</Link>
              <span className="text-sam-cream/50">·</span>
              <span className="text-sam-cream/80">{b.category}</span>
              <span className="text-sam-cream/50">·</span>
              <span className="flex items-center gap-1"><StarRating value={b.avg_rating} size={15} /> <span className="text-sam-cream/80">{b.avg_rating > 0 ? `${b.avg_rating} (${b.review_count})` : "New"}</span></span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Main */}
        <div className="space-y-8">
          {highlight && (
            <div className="rounded-xl border border-sam-crimson/30 bg-sam-crimson/5 px-4 py-3 flex items-center gap-2 animate-pulse" data-testid="assistant-highlight-banner">
              <Sparkles className="h-4 w-4 text-sam-crimson shrink-0" />
              <span className="text-sm text-sam-navy font-semibold">The SAM Assistant sent you here — here's <span className="text-sam-crimson">{b.name}</span>.</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={toggleSave} data-testid="save-business-btn" variant={saved ? "default" : "outline"} className={saved ? "bg-sam-crimson hover:bg-sam-crimson/90" : ""}>
              <Heart className={`h-4 w-4 mr-2 ${saved ? "fill-current" : ""}`} />{saved ? "Saved" : "Save"}
            </Button>
            {b.features?.phone && b.phone && <Button variant="outline" asChild><a href={`tel:${b.phone}`} onClick={trackCall} data-testid="listing-call-btn"><Phone className="h-4 w-4 mr-2" />{b.phone}</a></Button>}
            <ShareButton businessId={b.id} title={b.name} text={b.short_description || `Check out ${b.name} on Saving America Money`} label="Share this business" testid="share-business-btn" />
            {isOwner && <Button variant="outline" asChild><Link to="/dashboard"><ShoppingBag className="h-4 w-4 mr-2" />Manage listing</Link></Button>}
          </div>

          {isOwner && <UpgradeNudge b={b} />}

          <section>
            <h2 className="font-display text-xl font-bold text-sam-navy mb-2">About</h2>
            <p className="text-sam-slate leading-relaxed">{b.full_description || b.short_description}</p>
            {b.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {b.tags.map((t) => <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-sam-navy/5 text-sam-navy font-semibold">{t}</span>)}
              </div>
            )}
          </section>

          {b.postcard_deal_headline && (
            <section className="rounded-2xl border-2 border-dashed border-sam-crimson/40 bg-sam-crimson/5 p-5">
              <div className="flex items-start gap-3">
                <Ticket className="h-6 w-6 text-sam-crimson shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-sam-crimson">Postcard Deal</div>
                  <div className="font-display text-lg font-bold text-sam-navy">{b.postcard_deal_headline}</div>
                  {b.postcard_deal_details && <p className="text-sm text-sam-slate mt-1">{b.postcard_deal_details}</p>}
                  <DealCountdown expires={b.postcard_deal_expires} />
                </div>
                <div className="grid place-items-center rounded-lg bg-white p-2 border"><QrCode className="h-10 w-10 text-sam-navy" /></div>
              </div>
            </section>
          )}

          {b.features?.products && b.products?.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold text-sam-navy mb-3">Products & Services</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {b.products.map((p, i) => (
                  <div key={`${p.name}-${p.price}-${i}`} className="rounded-xl border border-border p-4 hover:border-sam-amber transition">
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold text-sam-navy">{p.name}</span>
                      <span className="font-mono text-sam-crimson font-bold">{p.price}</span>
                    </div>
                    {p.desc && <p className="text-sm text-sam-slate mt-1">{p.desc}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          <Separator />

          {/* Reviews */}
          <section>
            <h2 className="font-display text-xl font-bold text-sam-navy mb-4">Reviews ({reviews.length})</h2>
            {summary && (
              <div className="rounded-xl border border-sam-navy/15 bg-sam-navy/[0.03] p-4 mb-5" data-testid="ai-review-summary">
                <div className="flex items-center gap-2 text-sam-crimson text-xs font-bold uppercase tracking-wide mb-1"><Sparkles className="h-4 w-4" />What customers are saying</div>
                <p className="text-sm text-sam-slate leading-relaxed">{summary}</p>
              </div>
            )}
            <div className="rounded-xl border border-border p-4 mb-6 bg-card">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm font-semibold text-sam-navy">Your rating:</span>
                <StarRating value={rating} size={24} onChange={setRating} />
              </div>
              <Textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience…" data-testid="review-text" />
              <Button className="mt-3 bg-sam-navy hover:bg-sam-navy2" onClick={submitReview} disabled={busy} data-testid="submit-review-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Post Review
              </Button>
            </div>
            <div className="space-y-5">
              {reviews.map((r) => (
                <div key={r.id} className="flex gap-3" data-testid={`review-${r.id}`}>
                  <Avatar className="h-9 w-9"><AvatarImage src={r.user_picture} /><AvatarFallback className="bg-sam-navy text-sam-cream text-xs">{r.user_name?.[0]}</AvatarFallback></Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-semibold text-sam-navy text-sm">{r.user_name}</span>
                        <StarRating value={r.rating} size={13} className="mt-0.5" />
                      </div>
                      <div className="flex items-center gap-2">
                        {user && user.user_id === r.user_id && <button onClick={() => del(r.id)} className="text-slate-400 hover:text-sam-crimson" data-testid={`delete-review-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                        {user && <button onClick={() => flag(r.id)} className="text-slate-400 hover:text-sam-amber"><Flag className="h-3.5 w-3.5" /></button>}
                      </div>
                    </div>
                    {r.text && <p className="text-sm text-sam-slate mt-1">{r.text}</p>}
                    {r.owner_reply && (
                      <div className="mt-2 rounded-lg bg-sam-navy/5 border-l-2 border-sam-crimson p-2.5">
                        <div className="text-xs font-bold text-sam-crimson">Owner response</div>
                        <p className="text-sm text-sam-slate">{r.owner_reply}</p>
                      </div>
                    )}
                    {isOwner && !r.owner_reply && <ReplyBox onSend={(t) => reply(r.id, t)} />}
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <p className="text-sm text-sam-slate">Be the first to review {b.name}.</p>}
            </div>
          </section>

          <Separator />

          {/* Comments */}
          <section>
            <h2 className="font-display text-xl font-bold text-sam-navy mb-4 flex items-center gap-2"><MessageSquare className="h-5 w-5" />Community Q&A ({comments.length})</h2>
            <div className="flex gap-2 mb-5">
              <Textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Ask a question or leave a comment…" data-testid="comment-text" className="min-h-[44px]" />
              <Button className="bg-sam-crimson hover:bg-sam-crimson/90 self-end" onClick={submitComment} data-testid="submit-comment-btn">Post</Button>
            </div>
            <div className="space-y-3">
              {topComments.map((c) => (
                <div key={c.id} className="rounded-lg border border-border p-3" data-testid={`comment-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6"><AvatarFallback className="bg-sam-navy text-sam-cream text-[10px]">{c.user_name?.[0]}</AvatarFallback></Avatar>
                    <span className="text-sm font-semibold text-sam-navy">{c.user_name}</span>
                  </div>
                  <p className="text-sm text-sam-slate mt-1.5">{c.text}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {b.features?.ai_chat && <BizChat b={b} />}
          {!b.features?.phone && <ContactForm b={b} refCode={refCode} />}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-sam-crimson shrink-0 mt-0.5" /><div className="text-sm"><div className="font-semibold text-sam-navy">{b.address_line}</div><div className="text-sam-slate">{b.city}, {b.state_abbr} {b.zip}</div></div></div>
            {b.features?.phone && b.phone && <div className="flex items-center gap-3 text-sm"><Phone className="h-5 w-5 text-sam-crimson" /><a href={`tel:${b.phone}`} onClick={trackCall} className="text-sam-navy hover:underline" data-testid="listing-sidebar-phone">{b.phone}</a></div>}
            {b.has_links?.website && <div className="flex items-center gap-3 text-sm"><Globe className="h-5 w-5 text-sam-crimson" /><a href={outUrl("website")} target="_blank" rel="noreferrer" className="text-sam-navy hover:underline truncate" data-testid="listing-website-link">Website</a></div>}
            {b.has_links?.facebook && <div className="flex items-center gap-3 text-sm"><Facebook className="h-5 w-5 text-sam-crimson" /><a href={outUrl("facebook")} target="_blank" rel="noreferrer" className="text-sam-navy hover:underline" data-testid="listing-facebook-link">Facebook</a></div>}
            {b.has_links?.instagram && <div className="flex items-center gap-3 text-sm"><Instagram className="h-5 w-5 text-sam-crimson" /><a href={outUrl("instagram")} target="_blank" rel="noreferrer" className="text-sam-navy hover:underline" data-testid="listing-instagram-link">Instagram</a></div>}
            <Separator />
            <div className="flex items-start gap-3"><Clock className="h-5 w-5 text-sam-crimson shrink-0 mt-0.5" /><div className="text-sm space-y-0.5 text-sam-slate">
              <div><span className="font-semibold text-sam-navy">Mon–Fri</span> {b.hours_mon_fri || "—"}</div>
              <div><span className="font-semibold text-sam-navy">Sat</span> {b.hours_sat || "—"}</div>
              <div><span className="font-semibold text-sam-navy">Sun</span> {b.hours_sun || "—"}</div>
            </div></div>
            {(b.latitude && b.longitude) && (
              <>
                <Separator />
                <iframe title="map" className="w-full h-40 rounded-lg border" loading="lazy"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${b.longitude - 0.01}%2C${b.latitude - 0.01}%2C${b.longitude + 0.01}%2C${b.latitude + 0.01}&layer=mapnik&marker=${b.latitude}%2C${b.longitude}`} />
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function ReplyBox({ onSend }) {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState("");
  if (!open) return <button onClick={() => setOpen(true)} className="text-xs font-semibold text-sam-crimson mt-1">Reply as owner</button>;
  return (
    <div className="mt-2 flex gap-2">
      <Textarea value={t} onChange={(e) => setT(e.target.value)} className="min-h-[38px] text-sm" placeholder="Write a public reply…" />
      <Button size="sm" className="bg-sam-navy self-end" onClick={() => { onSend(t); setOpen(false); setT(""); }}>Send</Button>
    </div>
  );
}
